import { yupResolver } from '@hookform/resolvers/yup';
import React, { useCallback, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';

import { NumberInput } from '@/components/NumberInput';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

export interface CashflowStream {
    id: string;
    name: string;
    type: 'contribution' | 'withdrawal';
    frequency:
        | 'one-time'
        | 'every-week'
        | 'every-2-weeks'
        | 'every-month'
        | 'every-3-months'
        | 'every-6-months'
        | 'every-year';
    amount: number;
    startDate: string;
    adjustForInflation: boolean;
    inflationRate: number;
    account: string;
    repeatUntil: 'cancel' | 'specific-date' | 'occurrences';
    repeatEndDate?: string;
    repeatOccurrences?: number;
}

export interface Account {
    id: string;
    name: string;
}
interface EditCashflowDialogProps {
    cashflow: CashflowStream;
    onSave: (updatedStream: CashflowStream) => void;
    onCancel: () => void;
    accounts: Account[];
    open: boolean;
}

// Validation schema
const formSchema = Yup.object({
    id: Yup.string(),
    name: Yup.string()
        .required('Name is required')
        .trim()
        .min(1, 'Name cannot be empty')
        .max(64, 'Name cannot exceed 64 characters'),
    type: Yup.string().oneOf(['contribution', 'withdrawal'], 'Type is required').required('Type is required'),
    amount: Yup.number()
        .typeError('Amount must be a number')
        .required('Amount is required')
        .positive('Amount must be positive')
        .min(0.01, 'Amount must be at least $0.01'),
    frequency: Yup.string()
        .oneOf(
            [
                'one-time',
                'every-week',
                'every-2-weeks',
                'every-month',
                'every-3-months',
                'every-6-months',
                'every-year',
            ],
            'Frequency is required',
        )
        .required('Frequency is required'),
    startDate: Yup.string().required('Start date is required'),
    adjustForInflation: Yup.boolean(),
    inflationRate: Yup.number()
        .typeError('Inflation rate must be a number')
        .when(['adjustForInflation'], {
            is: (adjustForInflation: boolean) => adjustForInflation,
            then: () =>
                Yup.number()
                    .required('Inflation rate is required when adjusting for inflation')
                    .min(-99, 'Inflation rate must be greater than -99%'),
            otherwise: () => Yup.number().notRequired(),
        }),
    repeatUntil: Yup.string()
        .oneOf(['cancel', 'specific-date', 'occurrences'])
        .when('frequency', {
            is: (frequency: string) => frequency !== 'one-time',
            then: () => Yup.string().required('Repeat until option is required'),
            otherwise: () => Yup.string().optional(),
        }),
    repeatEndDate: Yup.string().when(['frequency', 'repeatUntil'], {
        is: (frequency: string, repeatUntil: string) => frequency !== 'one-time' && repeatUntil === 'specific-date',
        then: () =>
            Yup.string()
                .required('End date is required')
                .test('is-after-start', 'End date must be after start date', function (value) {
                    const startDate = this.parent.startDate;
                    if (!value || !startDate) return true; // Let required validation handle empty values
                    return new Date(value) > new Date(startDate);
                }),
        otherwise: () => Yup.string().notRequired(),
    }),
    repeatOccurrences: Yup.number().when(['frequency', 'repeatUntil'], {
        is: (frequency: string, repeatUntil: string) => frequency !== 'one-time' && repeatUntil === 'occurrences',
        then: () =>
            Yup.number()
                .typeError('Number of occurrences must be a number')
                .required('Number of occurrences is required')
                .positive('Must be positive')
                .integer('Must be a whole number'),
        otherwise: () => Yup.number().notRequired(),
    }),
    account: Yup.string()
        .required('Account is required')
        .test(
            'not-portfolio-level-for-contributions',
            'Portfolio Level is not allowed for contributions',
            function (value) {
                const type = this.parent.type;
                if (type === 'contribution' && value === 'portfolio_level') {
                    return false;
                }
                return true;
            },
        ),
});

type FormValues = Yup.InferType<typeof formSchema>;

export function EditCashflowDialog({ cashflow, onSave, onCancel, accounts, open }: EditCashflowDialogProps) {
    const nameInputRef = useRef<HTMLInputElement | null>(null);

    const getDefaultStartDatePlusOneYear = useCallback((startDate: string) => {
        if (!startDate) return '';
        const date = new Date(startDate);
        date.setFullYear(date.getFullYear() + 1);
        return date.toISOString().split('T')[0]; // Format as YYYY-MM-DD
    }, []);

    const getDefaultValues = useCallback(
        (cfs: CashflowStream) => ({
            id: cfs.id,
            name: cfs.name,
            type: cfs.type,
            amount: cfs.amount,
            frequency: cfs.frequency,
            startDate: cfs.startDate,
            adjustForInflation: cfs.adjustForInflation,
            inflationRate: cfs.inflationRate,
            account: cfs.account,
            repeatUntil: cfs.frequency === 'one-time' ? undefined : cfs.repeatUntil || 'cancel',
            repeatEndDate: cfs.repeatEndDate || getDefaultStartDatePlusOneYear(cfs.startDate),
            repeatOccurrences: cfs.repeatOccurrences || undefined,
        }),
        [getDefaultStartDatePlusOneYear],
    );

    const form = useForm<FormValues>({
        resolver: yupResolver(formSchema),
        defaultValues: getDefaultValues(cashflow),
        mode: 'onSubmit',
    });

    useEffect(() => {
        form.reset(getDefaultValues(cashflow));
    }, [cashflow, form, getDefaultValues]);

    useEffect(() => {
        // Focus the name input after the modal opens
        const timer = setTimeout(() => {
            nameInputRef.current?.focus();
        }, 100);

        return () => clearTimeout(timer);
    }, []);

    const handleSave = (data: FormValues) => {
        const updatedCashflow: CashflowStream = {
            ...cashflow,
            ...data,
        };
        onSave(updatedCashflow);
    };

    const frequency = form.watch('frequency');
    const isOneTime = frequency === 'one-time';
    const adjustForInflation = form.watch('adjustForInflation');
    const repeatUntil = form.watch('repeatUntil');

    // Auto-select "Until I cancel" when switching to a recurring frequency
    useEffect(() => {
        if (!isOneTime && !repeatUntil) {
            form.setValue('repeatUntil', 'cancel');
        }
    }, [frequency, isOneTime, repeatUntil, form]);

    const isAdding = !cashflow.id;
    const modalTitle = isAdding ? 'Add Cashflow Stream' : 'Edit Cashflow Stream';

    return (
        <>
            <Dialog open={open} onOpenChange={(newOpen) => !newOpen && onCancel()}>
                <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{modalTitle}</DialogTitle>
                        <DialogDescription></DialogDescription>
                    </DialogHeader>
                    <div className="scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 hover:scrollbar-thumb-gray-400 px-1">
                        <Form {...form}>
                            <form id="cashflow-form" onSubmit={form.handleSubmit(handleSave)} className="space-y-4">
                                {/* Basic Information Section */}
                                <div className="space-y-3">
                                    <FormField
                                        control={form.control}
                                        name="name"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>Name</FormLabel>
                                                <FormControl>
                                                    <Input
                                                        placeholder="Enter cashflow name"
                                                        {...field}
                                                        ref={(el) => {
                                                            field.ref(el);
                                                            (nameInputRef as any).current = el;
                                                        }}
                                                    />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />

                                    <FormField
                                        control={form.control}
                                        name="amount"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>Amount ($)</FormLabel>
                                                <FormControl>
                                                    <NumberInput
                                                        placeholder="Enter amount"
                                                        {...field}
                                                        onChange={(e) => field.onChange(Number(e.target.value))}
                                                    />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />

                                    <FormField
                                        control={form.control}
                                        name="type"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>Type</FormLabel>
                                                <Select onValueChange={field.onChange} value={field.value}>
                                                    <FormControl>
                                                        <SelectTrigger>
                                                            <SelectValue placeholder="Select type" />
                                                        </SelectTrigger>
                                                    </FormControl>
                                                    <SelectContent position="popper" sideOffset={4}>
                                                        <SelectItem value="contribution">Contribution</SelectItem>
                                                        <SelectItem value="withdrawal">Withdrawal</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />

                                    <FormField
                                        control={form.control}
                                        name="startDate"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>Start Date</FormLabel>
                                                <FormControl>
                                                    <Input type="date" {...field} />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                </div>

                                {/* Frequency Section */}
                                <div className="space-y-3 border-t pt-4">
                                    <FormField
                                        control={form.control}
                                        name="frequency"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>Frequency</FormLabel>
                                                <Select onValueChange={field.onChange} value={field.value}>
                                                    <FormControl>
                                                        <SelectTrigger>
                                                            <SelectValue placeholder="Select frequency" />
                                                        </SelectTrigger>
                                                    </FormControl>
                                                    <SelectContent position="popper" sideOffset={4}>
                                                        <SelectItem value="one-time">One-time</SelectItem>
                                                        <SelectItem value="every-week">Every week</SelectItem>
                                                        <SelectItem value="every-2-weeks">Every 2 weeks</SelectItem>
                                                        <SelectItem value="every-month">Every month</SelectItem>
                                                        <SelectItem value="every-3-months">Every 3 months</SelectItem>
                                                        <SelectItem value="every-6-months">Every 6 months</SelectItem>
                                                        <SelectItem value="every-year">Every year</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />

                                    {!isOneTime && (
                                        <>
                                            <FormField
                                                control={form.control}
                                                name="repeatUntil"
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <FormLabel>Until</FormLabel>
                                                        <Select
                                                            onValueChange={field.onChange}
                                                            value={field.value || ''}
                                                        >
                                                            <FormControl>
                                                                <SelectTrigger>
                                                                    <SelectValue placeholder="Select until option" />
                                                                </SelectTrigger>
                                                            </FormControl>
                                                            <SelectContent position="popper" sideOffset={4}>
                                                                <SelectItem value="cancel">I cancel</SelectItem>
                                                                <SelectItem value="specific-date">
                                                                    Specific end date
                                                                </SelectItem>
                                                                <SelectItem value="occurrences">
                                                                    Number of occurrences
                                                                </SelectItem>
                                                            </SelectContent>
                                                        </Select>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />

                                            {repeatUntil === 'specific-date' && (
                                                <FormField
                                                    control={form.control}
                                                    name="repeatEndDate"
                                                    render={({ field }) => (
                                                        <FormItem>
                                                            <FormLabel>End Date</FormLabel>
                                                            <FormControl>
                                                                <Input type="date" {...field} />
                                                            </FormControl>
                                                            <FormMessage />
                                                        </FormItem>
                                                    )}
                                                />
                                            )}

                                            {repeatUntil === 'occurrences' && (
                                                <FormField
                                                    control={form.control}
                                                    name="repeatOccurrences"
                                                    render={({ field }) => (
                                                        <FormItem>
                                                            <FormLabel>Number of Occurrences</FormLabel>
                                                            <FormControl>
                                                                <Input
                                                                    type="number"
                                                                    min="1"
                                                                    placeholder="Enter number of occurrences"
                                                                    {...field}
                                                                    value={field.value || ''}
                                                                    onChange={(e) =>
                                                                        field.onChange(Number(e.target.value))
                                                                    }
                                                                />
                                                            </FormControl>
                                                            <FormMessage />
                                                        </FormItem>
                                                    )}
                                                />
                                            )}
                                        </>
                                    )}
                                </div>

                                {/* Inflation Section */}
                                <div className="space-y-3 border-t pt-4">
                                    <FormField
                                        control={form.control}
                                        name="adjustForInflation"
                                        render={({ field }) => (
                                            <FormItem className="flex flex-row items-start space-y-0 space-x-3">
                                                <FormControl>
                                                    <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                                                </FormControl>
                                                <div className="space-y-1 leading-none">
                                                    <FormLabel>Adjust for inflation</FormLabel>
                                                </div>
                                            </FormItem>
                                        )}
                                    />

                                    {adjustForInflation && (
                                        <FormField
                                            control={form.control}
                                            name="inflationRate"
                                            render={({ field }) => (
                                                <FormItem>
                                                    <FormLabel>Inflation Rate (%)</FormLabel>
                                                    <FormControl>
                                                        <NumberInput
                                                            placeholder="Enter inflation rate"
                                                            {...field}
                                                            suffix="%"
                                                            onChange={(e) =>
                                                                field.onChange(Number(e.target.value.split('%')[0]))
                                                            }
                                                        />
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                    )}
                                </div>

                                {/* Account Section */}
                                <div className="border-t pt-4">
                                    <FormField
                                        control={form.control}
                                        name="account"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>Account</FormLabel>
                                                <Select onValueChange={field.onChange} value={field.value}>
                                                    <FormControl>
                                                        <SelectTrigger>
                                                            <SelectValue placeholder="Select account" />
                                                        </SelectTrigger>
                                                    </FormControl>
                                                    <SelectContent position="popper" sideOffset={4}>
                                                        {form.watch('type') === 'withdrawal' && (
                                                            <SelectItem key={0} value="portfolio_level">
                                                                Portfolio Level
                                                            </SelectItem>
                                                        )}

                                                        {accounts.map((account) => (
                                                            <SelectItem key={account.id} value={account.id}>
                                                                {account.name}
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                </div>
                            </form>
                        </Form>
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            type="button"
                            onClick={onCancel}
                            onMouseDown={(e) => e.preventDefault()}
                        >
                            Cancel
                        </Button>
                        <Button variant="default" type="submit" form="cashflow-form">
                            {isAdding ? 'Add Cashflow' : 'Save Changes'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}
